import React, { useEffect, useMemo, useState } from "react";
import { DevModeStyles, devModeStyles } from "./dev.mode.styles";
import { GetterSetter, makeUseStateChild } from "@me8eon/context";
import { CommonFeatureFlag, FeatureFlags, isOptionsFeatureFlag, OptionsFeatureFlag, useFeatureFlags } from "@me8eon/featureflags";
import { useAttributeEditorComponents } from "@me8eon/editors";
import { useCommonComponents } from "@me8eon/components";
import { EnvironmentConfig } from "@me8eon/config";
import { useAccessToken } from "@me8eon/react_authentication";
import { jwtTokenName } from "@me8eon/access_token";
import { NameAnd } from "@me8eon/record_utils";
import { ErrorsOr, isErrors, isValue } from "@me8eon/errors";

export function DevModeFeatureFlags() {
    const ops = useState("local");
    const selected = ops[0];
    const { TwoColumnAndRest, SelectableButtonBar } = useCommonComponents();
    return <><SelectableButtonBar options={["local", "all"]} selectedOps={ops} rootId="feature.flags"></SelectableButtonBar>
        {selected === "local" && <LocalDevModeFeatureFlags />}
        {selected === "all" && <FeatureFlagsTable />}
    </>;
}

export async function fetchFeatureFlags(t?: string): Promise<ErrorsOr<NameAnd<ErrorsOr<FeatureFlags>>>> {
    if (!t) return { errors: ["No access token provided"] };
    try {
        const headers = { Authorization: `Bearer ${t}` };
        const resp = await fetch(`${EnvironmentConfig.gatewayURL}/ffs`, { headers });
        if (!resp.ok) return { errors: [`Failed to fetch feature flags: ${resp.status} ${resp.statusText} ${await resp.text()}`] };
        return { value: await resp.json() };
    } catch (e: any) {
        return { errors: [`Error fetching feature flags: ${e.message}`], extras: e };
    }
}

export type FfsSummary = {
    description: string
    values: NameAnd<string | boolean>
}

export type TransformResult = {
    envs: string[]
    ffs: NameAnd<FfsSummary>

}

export function transformFeatureFlags(envToFfs?: NameAnd<ErrorsOr<FeatureFlags>>): TransformResult {
    if (!envToFfs) return { envs: [], ffs: {} };
    const envs: string[] = Object.keys(envToFfs);
    const ffs: NameAnd<FfsSummary> = {};
    for (const [env, ffValue] of Object.entries(envToFfs)) {
        if (isValue(ffValue)) {
            for (const [ffName, ffDetails] of Object.entries(ffValue.value)) {
                const newValue: FfsSummary = ffs[ffName] || { description: ffs, values: {} };
                newValue.values[env] = ffDetails.value;
                ffs[ffName] = newValue;
            }
        }
    }
    return { envs, ffs };
}

export type TransformedProps = {
    transformed: TransformResult
}

export function Transformed({ transformed }: TransformedProps) {
    const { envs, ffs } = transformed;
    return <>
        <table>
            <thead>
            <tr>
                <th>Feature Flag</th>
                <th>Description</th>
                {/*{envs.map(env => <th key={env}>{env}</th>)}*/}
            </tr>
            </thead>
            {/*<tbody>*/}
            {/*{Object.keys(ffs).sort().map(ffName => {*/}
            {/*    const ff = ffs[ffName];*/}
            {/*    return <tr key={ffName}>*/}
            {/*        <td>{ffName}</td>*/}
            {/*        <td>{ff.description}</td>*/}
            {/*        /!*{envs.map(env => <td key={env}>{JSON.stringify(ff.values[env])}</td>)}*!/*/}
            {/*    </tr>;*/}
            {/*})}*/}
            {/*</tbody>*/}
        </table>
        <pre>{JSON.stringify(transformed, null, 2)}</pre>
    </>;
}

export const sampleFFs: NameAnd<ErrorsOr<FeatureFlags>> = {
    "http:localhost:8080": {
        "errors": [
            "Error fetching feature flags: net::ERR_CONNECTION_REFUSED at http:localhost:8080/ff.json",
        ],
    },
    "https://dev-myhelp.eon.com": {
        "value": {
            "dataAsJson": {
                "value": false,
                "description": "show data as json",
            },
            "jql": {
                "description": "use JQL in the assistant",
                "value": true,
            },
            "newArchitecture": {
                "description": "Use the new architecture",
                "value": true,
            },
            "theme": {
                "description": "Choose the theme",
                "value": "default",
                "options": [
                    "default",
                    "eon",
                    "bayernwerk",
                ],
            },
            "order_it": {
                "description": "order it",
                "value": "orderIt",
                "options": [
                    "default",
                    "orderIt",
                    "testForVasu",
                ],
            },
            "accessToken": {
                "value": true,
                "description": "Use access token store to get access tokens",
            },
            "eonOrPlain": {
                "value": "plain",
                "options": [
                    "eon",
                    "plain",
                ],
                "description": "Use eon components or plain components",
            },
            "newSearch": {
                "description": "Use the new search",
                "value": true,
            },
            "searchParser": {
                "value": false,
                "description": "Search Parser / Intent Parser",
            },
            "compass": {
                "description": "compass feature flag",
                "value": false,
            },
        },
    },
};

export function FeatureFlagsTable() {
    const token = useAccessToken(jwtTokenName);
    const { ClipHeight } = useCommonComponents();
    const [ffs, setFfs] = useState<ErrorsOr<NameAnd<ErrorsOr<FeatureFlags>>>>({ value: {} });

    useEffect(() => {
        token.then(t => fetchFeatureFlags(t)).then(ffs =>
            setFfs(ffs));
    }, []);
    if (isErrors(ffs)) return <span>Error fetching feature flags: {ffs.errors.join(", ")}</span>;
    const envs: string[] = Object.keys(ffs.value || []);

    const transformed: any = useMemo(() => {
        const result: any = {};
        for (const [env, ffValuesInEnv] of Object.entries(ffs.value)) {
            if (isValue(ffValuesInEnv)) {
                for (const [ffName, ffDetails] of Object.entries(ffValuesInEnv.value)) {
                    const ffSummary = result[ffName] || { description: ffDetails.description, values: {} };
                    ffSummary.values[env] = ffDetails.value;
                    result[ffName] = ffSummary;
                }
            }
        }
        for (const [env, ffValuesInEnv] of Object.entries(ffs.value)) {
            if (isErrors(ffValuesInEnv)) {
                for (const [name, values] of Object.entries(result)) {
                    const ffSummary = values as FfsSummary;
                    ffSummary.values[env] = ffValuesInEnv.errors.join(", ");
                }
            }
        }
        return result;
    }, [ffs]);
    if (envs.length === 0) return <span>Loading...</span>;
    return <>
        <table>
            <thead>
            <tr>
                <th>Feature Flag</th>
                <th>Description</th>
                {envs.map(env => <th key={env}>{env}</th>)}
            </tr>
            </thead>
            <tbody>
            {Object.keys(transformed).sort().map(ffName => {
                    const ff = transformed[ffName];
                    return <tr key={ffName}>
                        <td>{ffName}</td>
                        <td>{ff.description}</td>
                        {envs.map(env => <td key={env}>{JSON.stringify(ff.values[env])}</td>)}
                    </tr>;
                },
            )}
            </tbody>
        </table>
        <ClipHeight maxHeight="600px" scrollable={true}>
            <pre>{JSON.stringify(ffs, null, 2)}</pre>
        </ClipHeight>
    </>;
}

export function LocalDevModeFeatureFlags() {
    const ops = useFeatureFlags();
    const [featureFlags] = ops;
    return (
        <table>
            <thead>
            <tr>
                <th>Feature Flag</th>
                <th>Value</th>
                <th>Description</th>
            </tr>
            </thead>
            <tbody>
            {Object.keys(featureFlags).sort().map(name => {
                    const ff = featureFlags[name];
                    return isOptionsFeatureFlag(ff)
                        ? <DisplayOptionsFeatureFlag key={name} name={name} ff={ff} styles={devModeStyles} />
                        : <DisplayBooleanFeatureFlag key={name} name={name} ff={ff} styles={devModeStyles} />;
                },
            )}
            </tbody>
        </table>
    );
}

export type OneFeatureFlagProps<T> = {
    name: string
    ff: CommonFeatureFlag<T>
    styles: DevModeStyles
}

export function DisplayBooleanFeatureFlag({ styles, name, ff }: OneFeatureFlagProps<boolean>) {
    const useValue = makeUseStateChild(useFeatureFlags, l => l.focusOn(name).focusOn("value"));
    const [featureFlag, setFeatureFlag] = useValue();
    const {
        checkboxStyle,
    } = styles;
    return (
        <tr>
            <td>{name}</td>
            <td>
                <input
                    type="checkbox"
                    checked={featureFlag as boolean}
                    onChange={() => setFeatureFlag(v => !v)}
                    style={checkboxStyle}
                />
            </td>
            <td>{ff.description}</td>
        </tr>);
}

export function DisplayOptionsFeatureFlag({ name, ff }: OneFeatureFlagProps<string>) {
    const useValue = makeUseStateChild(useFeatureFlags, l => l.focusOn(name).focusOn("value"));
    const ffOps = useValue() as GetterSetter<string>;

    const { options, description } = ff as OptionsFeatureFlag;
    const { Options } = useAttributeEditorComponents();
    return <tr>
        <td>{name}</td>
        <td>
            <Options rootId="devmode" ops={ffOps} options={options} attribute={""} translate={false} ariaLabel={description} />
        </td>
        <td>{description}</td>
    </tr>;
}




